import React from "react";
import { Rating } from "primereact/rating";
import { Tag } from "primereact/tag";

import { ProspectDetail } from "../../../../types/onboarding";
import {
  LEAD_SOURCE_NEEDING_DETAIL,
  leadSourceLabel,
  salutationLabel,
  signMethodLabel,
} from "../../../../constants/contractSigning";
import DocumentReference from "./DocumentReference";
import {
  needsWallboxClarification,
  stationCounts,
} from "../../../../utils/stationCounts";
import { formatInstant } from "../../../../utils/instantFormat";
import { isOnboardingCompleted } from "../../../../utils/onboardingStates";

// What the customer entered for an unlisted wallbox (CWA-229), e.g. "11 kW · AC · Socket".
const wallboxSpecs = (prospect: ProspectDetail): string =>
  [
    prospect.wallbox_power_kw != null ? `${prospect.wallbox_power_kw} kW` : null,
    prospect.wallbox_current_type,
    prospect.wallbox_connection_type === "CABLE"
      ? "Fixed cable"
      : prospect.wallbox_connection_type === "SOCKET"
        ? "Socket"
        : null,
  ]
    .filter(Boolean)
    .join(" · ");

interface ProspectFieldsProps {
  prospect: ProspectDetail;
}

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="col-12 md:col-6 mb-2">
    <div className="text-500 text-sm">{label}</div>
    <div className="font-medium">{value || value === 0 ? value : "—"}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-4">
    <h3 className="mt-0 mb-2">{title}</h3>
    <div className="grid">{children}</div>
  </div>
);

/**
 * Read-only display of every submitted prospect field (FR-007). Billing and
 * station addresses are shown distinctly; when the station address is the same
 * as billing, that is stated explicitly rather than repeating fields.
 */
/**
 * The station address on one line, in the form ops paste when creating the
 * station by hand (CWA-195): "Berliner Str. 38, 64823 Groß-Umstadt Germany".
 */
export const fullStationAddress = (prospect: ProspectDetail): string => {
  const [street, postcode, city, country] = prospect.station_same_as_billing
    ? [
        prospect.billing_street,
        prospect.billing_postcode,
        prospect.billing_city,
        prospect.billing_country,
      ]
    : [
        prospect.station_street,
        prospect.station_postcode,
        prospect.station_city,
        prospect.station_country,
      ];
  const place = [postcode, city, country].filter(Boolean).join(" ");
  return [street, place].filter(Boolean).join(", ");
};

const ProspectFields: React.FC<ProspectFieldsProps> = ({ prospect }) => {
  const isBusiness = prospect.customer_type === "BUSINESS";
  const counts = stationCounts(prospect);
  const rating = prospect.onboarding_rating;

  return (
    <div>
      <Section title="Contact">
        <Field label="First name" value={prospect.first_name} />
        <Field label="Last name" value={prospect.last_name} />
        <Field label="Email" value={prospect.email} />
        <Field label="Phone" value={prospect.telephone} />
        <Field label="eMAID" value={prospect.emaid} />
        <Field
          label="Customer type"
          value={isBusiness ? "Business" : "Private"}
        />
        {isBusiness && (
          <>
            <Field label="Company name" value={prospect.company_name} />
            <Field
              label="VAT required"
              value={prospect.vat_required ? "Yes" : "No"}
            />
            <Field label="VAT number" value={prospect.vat_number} />
          </>
        )}
      </Section>

      <Section title="Signing">
        <Field label="Salutation" value={salutationLabel(prospect.salutation)} />
        <Field
          label="Signature method"
          value={signMethodLabel(prospect.sign_method)}
        />
        <Field label="Lead source" value={leadSourceLabel(prospect.lead_source)} />
        {/* The free-text follow-up belongs to "Other" now, not to a referral
            (CWA-154 finding 2). Legacy prospects that answered the retired
            "Referral" still have text here, so it is shown for either. */}
        {(prospect.lead_source === LEAD_SOURCE_NEEDING_DETAIL ||
          prospect.lead_source === "REFERRAL") && (
          <Field
            label={
              prospect.lead_source === "REFERRAL" ? "Referred by" : "Their answer"
            }
            value={prospect.referred_by}
          />
        )}
        {/* Legacy free-text source, shown only when the structured lead source
            is absent (superseded by lead_source — see spec research R6). */}
        {!prospect.lead_source && prospect.discovery_source && (
          <Field label="How they found us" value={prospect.discovery_source} />
        )}
        {prospect.setup_fee_consent !== undefined && (
          <Field
            label="Setup-fee consent"
            value={
              prospect.setup_fee_consent
                ? `Given ${formatInstant(prospect.setup_fee_consent.accepted_at)} (${prospect.setup_fee_consent.consent_version})`
                : "No fee consent recorded"
            }
          />
        )}
      </Section>

      <Section title="Billing address">
        <Field label="Street" value={prospect.billing_street} />
        <Field label="Postcode" value={prospect.billing_postcode} />
        <Field label="City" value={prospect.billing_city} />
        <Field label="Country" value={prospect.billing_country} />
      </Section>

      <Section title="Station address">
        <Field
          label={
            prospect.station_same_as_billing
              ? "Full address (same as billing address)"
              : "Full address"
          }
          value={fullStationAddress(prospect)}
        />
        {!prospect.station_same_as_billing && (
          <>
            <Field label="Street" value={prospect.station_street} />
            <Field label="Postcode" value={prospect.station_postcode} />
            <Field label="City" value={prospect.station_city} />
            <Field label="Country" value={prospect.station_country} />
          </>
        )}
        <Field
          label="GPS coordinates"
          value={`${prospect.latitude}, ${prospect.longitude}`}
        />
      </Section>

      <Section title="Charging hardware">
        {/* One wallbox can provide several charging points, and eRound registers
            the wallbox (CWA-154 finding 1). No per-wallbox breakdown here: the
            detail response carries no station_evse_counts and this page fetches
            its own prospect, so the breakdown is a list-only affordance until
            that lands — see the contract doc §5. */}
        <Field label="Wallboxes" value={counts.stations || "—"} />
        <Field
          label="Charging points"
          value={
            <span className="flex align-items-center gap-2">
              {counts.chargePoints || "—"}
              {needsWallboxClarification(prospect) && (
                <Tag
                  severity="warning"
                  value="More than one wallbox — needs clarification"
                />
              )}
            </span>
          }
        />
        <Field label="Manufacturer" value={prospect.wallbox_manufacturer} />
        <Field label="Model" value={prospect.wallbox_model} />
        {prospect.wallbox_other && (
          <Field label="Wallbox specs (not in the catalogue)" value={wallboxSpecs(prospect)} />
        )}
        {/* The API already returns this as readable text ("Private household"),
            so there is nothing to map here. */}
        <Field
          label="Where the station is used"
          value={prospect.location_type || "Not provided"}
        />
        <Field
          label="Station IDs"
          value={
            prospect.technical_names?.length
              ? prospect.technical_names.join(", ")
              : "Not assigned"
          }
        />
      </Section>

      <Section title="Banking details">
        <Field label="Account holder" value={prospect.account_holder_name} />
        <Field label="IBAN" value={prospect.iban} />
        <Field label="BIC" value={prospect.bic} />
        <Field label="Bank name" value={prospect.bank_name} />
      </Section>

      {/* The customer's rating of their onboarding (CWA-207). Only a finished
          onboarding can be rated, so earlier stages show nothing at all. */}
      {rating ? (
        <Section title="Rating">
          <Field
            label="Stars"
            value={
              <span className="flex align-items-center gap-2">
                <Rating value={rating.stars} readOnly cancel={false} />
                {`${rating.stars} / 5`}
              </span>
            }
          />
          <Field label="Submitted" value={formatInstant(rating.submitted_at)} />
          <Field label="Comment" value={rating.comment} />
        </Section>
      ) : (
        isOnboardingCompleted(prospect.current_state) && (
          <Section title="Rating">
            <Field label="Stars" value="Not rated yet" />
          </Section>
        )
      )}

      {prospect.contract_draft_url && (
        <Section title="Documents">
          {/* Openable addresses are links; the document store's private ones are
              shown as copyable text instead (FR-013). Same treatment as the
              contract panel. */}
          <Field
            label="Contract draft"
            value={<DocumentReference reference={prospect.contract_draft_url} />}
          />
        </Section>
      )}
    </div>
  );
};

export default ProspectFields;
